import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  CreditCard,
  Dot,
  KeyRound,
  Mail,
  Pencil,
  Receipt,
  Tags,
  UserPlus,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetParentById } from '~entities/parent/api/get-parent-by-id';
import { useReportingParentOutstandingBalances } from '~entities/payment/api/reporting-parent-outstanding-balances';
import { type StudentSearch } from '~entities/student';
import { useGetOutstandingBalance } from '~entities/student/api/get-outstanding-balance';
import { useGetStudentsRelationsByParentId } from '~entities/student/api/get-student-relations-by-studentId';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { EditParentForm } from '~features/contacts/parent/parent-form';
import { ParentTags } from '~features/parents/ui/parent-tags';
import { ResetPassword } from '~features/students/ui/reset-password';
import { StudentByFamilyMobileCarousel } from '~features/students/ui/student-by-family-mobile-carousel';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { DataTableColumnHeaderWithFilter } from '~shared/components/table/header-with-filters';
import {
  calculateAgeFromISO,
  formatDateOnlySafe,
} from '~shared/helpers/datetime';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent, CardHeader } from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';

// Create a BalanceCell component
const BalanceCell = ({ studentId }: { studentId: string }) => {
  const { data: currencyDetails } = useGetCurrency();
  const { data: outstandingBalance } = useGetOutstandingBalance(studentId);

  const totalOutstanding = outstandingBalance?.totalOutstanding ?? '0.00';

  return (
    <div
      className={`px-4 ${
        Number(totalOutstanding) > 0 ? 'font-medium text-red-600' : ''
      }`}
    >
      {currencyDetails?.symbol ?? '$'}
      {totalOutstanding}
    </div>
  );
};

// Create a column helper
const columnHelper = createColumnHelper<StudentSearch>();

// Define columns
const cols = [
  columnHelper.accessor('name', {
    header: (info) => (
      <DataTableColumnHeaderWithFilter
        className='pl-4 text-black'
        info={info}
        title='Student Name'
      />
    ),
    cell: (info) => (
      <Button
        variant={'link'}
        className='max-w-full whitespace-normal text-custom-secondary-1'
      >
        <Link
          to={`/students/$studentId/details`}
          params={{
            studentId: info.row.original._id,
          }}
        >
          {info.getValue()}
        </Link>
      </Button>
    ),
  }),
  columnHelper.accessor('gender', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Gender'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
  columnHelper.accessor('active', {
    id: 'status',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Status'
      />
    ),
    cell: (info) => {
      const isActive = info.getValue();
      return (
        <div
          className={cn(
            'ml-auto flex w-fit items-center rounded-full pr-2 md:ml-4',
            {
              'bg-[#ECFDF3]': isActive,
              'bg-[#F2F4F7]': !isActive,
            },
          )}
        >
          <Dot
            className={cn({
              'text-[#14BA6D]': isActive,
              'text-[#6C778B]': !isActive,
            })}
            strokeWidth={4}
          />
          <span
            className={cn('font-medium', {
              'text-[#037847]': isActive,
              'text-[#364254]': !isActive,
            })}
          >
            {info.getValue() ? 'Active' : 'Inactive'}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor('dob', {
    id: 'Date of Birth',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Date of Birth'
      />
    ),
    cell: (info) => {
      const dob = info.getValue();
      if (!dob) return <div className='px-4'>-</div>;
      return (
        <div className='px-4'>
          {formatDateOnlySafe(dob, 'MM/dd/yyyy', 'Invalid date')}
        </div>
      );
    },
  }),
  columnHelper.accessor('dob', {
    id: 'age',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Age'
      />
    ),
    cell: (info) => {
      const { years, months } = calculateAgeFromISO(info.getValue());
      return (
        <div className='px-4'>
          {`${years} ${years > 1 ? 'years' : 'year'} ${months} ${months > 1 ? 'months' : 'month'}`}
        </div>
      );
    },
  }),
  columnHelper.accessor('_id', {
    id: 'balance',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Balance'
      />
    ),
    cell: (info) => <BalanceCell studentId={info.getValue()} />,
  }),
  columnHelper.accessor('tShirtSize', {
    id: 'costumeSize',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Costume Size'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
];

export function StudentByFamilyListTable({ parentId }: { parentId: string }) {
  const [showDialog, setShowDialog] = useState(false);
  const [showParentTagsDialog, setShowParentTagsDialog] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const { hasSuperAdminAccess, hasAdminAccess, isRolesReady } =
    useCurrentUserRole();
  const canViewFamilyBilling = isRolesReady && hasAdminAccess;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const { data: studentsData, isFetching } =
    useGetStudentsRelationsByParentId(parentId);

  const { data: parentData } = useGetParentById(parentId);
  const { data: parentOutstandingData } = useReportingParentOutstandingBalances(
    {
      parentId: canViewFamilyBilling ? parentId : undefined,
      format: 'json',
    },
  );
  const { data: currencyDetails } = useGetCurrency();

  const columns = useMemo(
    () =>
      canViewFamilyBilling
        ? cols
        : cols.filter((column) => column.id !== 'balance'),
    [canViewFamilyBilling],
  );

  const data: Array<StudentSearch> = studentsData ?? [];
  const parentTotalOutstanding =
    typeof parentOutstandingData === 'string'
      ? '0.00'
      : (parentOutstandingData?.totalOutstanding ?? '0.00');


  return (
    <>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <Card className='min-w-0 max-w-full'>
          <CardHeader className='p-4 sm:p-6'>
            <div className='flex justify-between gap-2'>
              <h3 className='min-w-0 text-lg font-semibold'>Family Details</h3>
              <DialogTrigger asChild>
                <Button variant={'secondary'} className='shrink-0'>
                  <Pencil />
                </Button>
              </DialogTrigger>
            </div>
          </CardHeader>
          <CardContent className='p-4 pt-0 sm:p-6 sm:pt-0'>
            <div className='flex min-w-0 flex-col'>
              {canViewFamilyBilling ? (
                <div className='mb-4 rounded-md border bg-muted/20 p-3'>
                  <div className='text-xs text-muted-foreground'>
                    Total Outstanding Balance
                  </div>
                  <div
                    className={cn('text-xl font-semibold', {
                      'text-red-600': Number(parentTotalOutstanding) > 0,
                      'text-green-600': Number(parentTotalOutstanding) <= 0,
                    })}
                  >
                    {currencyDetails?.symbol ?? '$'}
                    {parentTotalOutstanding}
                  </div>
                </div>
              ) : null}
              <dl className='grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm'>
                <dt className='font-medium'>Family Name:</dt>
                <dd className='min-w-0 break-words [overflow-wrap:anywhere]'>
                  {parentData?.familyName}
                </dd>

                <dt className='font-medium'>Parent Name:</dt>
                <dd className='min-w-0 break-words [overflow-wrap:anywhere]'>
                  {parentData?.name ?? ''}
                </dd>

                <dt className='font-medium'>Parent Email:</dt>
                <dd className='flex min-w-0 items-start gap-2'>
                  <Mail className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                  <a
                    href={`mailto:${parentData?.email}`}
                    className='min-w-0 break-words text-primary [overflow-wrap:anywhere] hover:underline'
                  >
                    {parentData?.email}
                  </a>
                </dd>
              </dl>

              <div className='mt-4 flex flex-wrap justify-end gap-2'>
                {hasSuperAdminAccess && (
                  <Button
                    className='shrink-0'
                    variant='secondary'
                    title='Reset Password'
                    aria-label='Reset Password'
                    onClick={() => {
                      setResetPasswordOpen(true);
                    }}
                  >
                    <KeyRound />
                    <span className='sr-only sm:not-sr-only sm:whitespace-nowrap'>
                      Reset Password
                    </span>
                  </Button>
                )}
                <Button
                  variant='secondary'
                  className='shrink-0'
                  title='Saved payment methods'
                  aria-label='Saved payment methods'
                  asChild
                >
                  <Link
                    to='/parents/$parentId/saved-payment-methods'
                    params={{ parentId }}
                  >
                    <CreditCard />
                    <span className='sr-only sm:not-sr-only sm:whitespace-nowrap'>
                      Saved payment methods
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
            <DialogDescription>
              Make changes to your profile here. Click save when you&apos;re
              done.
            </DialogDescription>
          </DialogHeader>
          <div className=''>
            {parentData ? (
              <EditParentForm
                studentId={studentsData?.[0]?._id ?? ''}
                parent={parentData}
                onCancel={() => {
                  setShowDialog(false);
                }}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
      <Card className='mt-6 min-w-0 max-w-full'>
        <CardContent className='p-4 sm:p-6'>
          <EnrollioTable
            ariaLabel='Family students'
            totalLabel='Students'
            filtersLayout='inline'
            filterHeader={(table) => (
              <EnrollioTableSearch
                placeholder='Search Student...'
                value={
                  (table.getColumn('name')?.getFilterValue() as
                    | string
                    | undefined) ?? ''
                }
                onChange={(value) => {
                  table.getColumn('name')?.setFilterValue(value);
                }}
              />
            )}
            className='h-auto min-w-0 max-w-full'
            columns={columns}
            data={data}
            rowCount={data.length}
            sorting={sorting}
            isUpdating={isFetching}
            onSortingChange={setSorting}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={{
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
            }}
            onPaginationChange={setPagination}
            manualPagination
            mobile={(table) => (
              <StudentByFamilyMobileCarousel
                table={table}
                isUpdating={isFetching}
              />
            )}
            toolbar={(table) => (
              <>
                <Button
                  variant='secondary'
                  className='h-8 shrink-0'
                  title='Parent Tags'
                  aria-label='Parent Tags'
                  onClick={() => {
                    setShowParentTagsDialog(true);
                  }}
                >
                  <Tags />
                  <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
                    Parent Tags
                  </span>
                </Button>
                {canViewFamilyBilling ? (
                  <Button variant='secondary' asChild className='h-8 shrink-0'>
                    <Link
                      to={'/parents/transactions-table'}
                      search={{ parentId }}
                      title='Total Transactions'
                      aria-label='Total Transactions'
                    >
                      <Receipt />
                      <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
                        Total Transactions
                      </span>
                    </Link>
                  </Button>
                ) : null}
                <Button variant='secondary' asChild className='h-8 shrink-0'>
                  <Link
                    to={'/register-student'}
                    search={{ parentId }}
                    title='Add New Student'
                    aria-label='Add New Student'
                  >
                    <UserPlus />
                    <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
                      Add New Student
                    </span>
                  </Link>
                </Button>
                <EnrollioTableColumnToggle table={table} />
              </>
            )}
          />
        </CardContent>
      </Card>

      <ResetPassword
        email={parentData?.email ?? ''}
        open={resetPasswordOpen}
        setOpen={setResetPasswordOpen}
      />

      {showParentTagsDialog && (
        <ParentTags
          open={showParentTagsDialog}
          onOpenChange={setShowParentTagsDialog}
          parentId={parentId}
          parentName={
            (parentData?.firstName ?? '') + ' ' + (parentData?.lastName ?? '')
          }
          isDemoParent={parentData?.isDemoData === true}
        />
      )}
    </>
  );
}
